import React, { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';
import { MESES, formatCLP, formatFecha, claveMes } from '../lib/format';

function Reports({ userId }) {
  const [data, setData] = useState({ bolsillos: [], movimientos: [], compromisos: [] });
  const [loading, setLoading] = useState(true);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

  useEffect(() => {
    if (!userId) return;
    const load = async () => {
      const [b, m, c] = await Promise.all([
        supabase.from('bolsillos').select('*').eq('user_id', userId),
        supabase.from('bolsillo_movimientos').select('*').eq('user_id', userId),
        supabase.from('compromisos').select('*').eq('user_id', userId),
      ]);
      setData({
        bolsillos: b.data || [],
        movimientos: m.data || [],
        compromisos: c.data || [],
      });
      setLoading(false);
    };
    load();
  }, [userId]);

  const { bolsillos, movimientos, compromisos } = data;
  const claveSeleccionada = `${selectedYear}-${String(selectedMonth).padStart(2, '0')}`;

  const filtered = movimientos.filter(m => claveMes(m.fecha) === claveSeleccionada);

  const totalIngresos = filtered.filter(m => m.tipo === 'ingreso').reduce((s, m) => s + Number(m.monto), 0);
  const totalEgresos = filtered.filter(m => m.tipo === 'egreso').reduce((s, m) => s + Number(m.monto), 0);
  const balance = totalIngresos - totalEgresos;

  const compsMes = compromisos.filter(c => claveMes(c.fecha) === claveSeleccionada);

  const bolsillosSummary = bolsillos.map(b => {
    const movs = filtered.filter(m => m.bolsillo_id === b.id);
    const ing = movs.filter(m => m.tipo === 'ingreso').reduce((s, m) => s + Number(m.monto), 0);
    const egr = movs.filter(m => m.tipo === 'egreso').reduce((s, m) => s + Number(m.monto), 0);
    return { ...b, ingresos: ing, egresos: egr, cantidad: movs.length };
  }).filter(b => b.cantidad > 0);

  if (loading) return (
    <div className="bolsillos-loading"><div className="loading-spinner"></div><p>Cargando...</p></div>
  );

  return (
    <div className="reports">
      <div className="page-header">
        <div>
          <h2>Reportes</h2>
          <p className="page-subtitle">Resumen mensual de tus movimientos</p>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 12, marginBottom: 24, flexWrap: 'wrap', alignItems: 'center' }}>
        <select value={selectedMonth} onChange={e => setSelectedMonth(parseInt(e.target.value))} style={{ width: 'auto' }}>
          {MESES.map((m, i) => <option key={i + 1} value={i + 1}>{m}</option>)}
        </select>
        <input
          type="number"
          value={selectedYear}
          onChange={e => setSelectedYear(parseInt(e.target.value))}
          style={{ width: 90 }}
        />
      </div>

      <div className="summary-cards" style={{ marginBottom: 24 }}>
        <div className="card">
          <div className="card-icon">📈</div>
          <div className="card-content">
            <h4>Ingresos</h4>
            <p className="card-amount positive">{formatCLP(totalIngresos)}</p>
          </div>
        </div>
        <div className="card">
          <div className="card-icon">📉</div>
          <div className="card-content">
            <h4>Egresos</h4>
            <p className="card-amount negative">{formatCLP(totalEgresos)}</p>
          </div>
        </div>
        <div className="card">
          <div className="card-icon">💰</div>
          <div className="card-content">
            <h4>Balance</h4>
            <p className={`card-amount ${balance >= 0 ? 'positive' : 'negative'}`}>{formatCLP(balance)}</p>
          </div>
        </div>
        <div className="card">
          <div className="card-icon">📌</div>
          <div className="card-content">
            <h4>Compromisos</h4>
            <p className="card-amount neutral">{compsMes.length} nuevos</p>
          </div>
        </div>
      </div>

      {bolsillosSummary.length > 0 && (
        <div className="recent-transactions">
          <h3>Por categoría</h3>
          <ul>
            {bolsillosSummary.map(b => (
              <li key={b.id} className="transaction-item">
                <span className="transaction-desc" style={{ fontWeight: 600 }}>{b.nombre}</span>
                <div style={{ display: 'flex', gap: 16, fontSize: '0.88rem', flexWrap: 'wrap' }}>
                  <span className="text-success">+{formatCLP(b.ingresos)}</span>
                  <span className="text-danger">-{formatCLP(b.egresos)}</span>
                  <span className="text-muted">{b.cantidad} mov.</span>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {filtered.length > 0 ? (
        <div className="recent-transactions">
          <h3>Detalle — {MESES[selectedMonth - 1]} {selectedYear} ({filtered.length})</h3>
          <ul>
            {[...filtered].sort((a, b) => b.fecha.localeCompare(a.fecha)).map(m => {
              const bolsillo = bolsillos.find(b => b.id === m.bolsillo_id);
              return (
                <li key={m.id} className="transaction-item">
                  <span className="transaction-desc">
                    <strong>{bolsillo?.nombre || '?'}</strong> — {m.descripcion || (m.tipo === 'ingreso' ? 'Entrada' : 'Salida')}
                    <span style={{ display: 'block', fontSize: '0.78rem', color: 'var(--text-muted)' }}>{formatFecha(m.fecha)}</span>
                  </span>
                  <span className={`transaction-amount ${m.tipo === 'ingreso' ? 'positive' : 'negative'}`}>
                    {m.tipo === 'ingreso' ? '+' : '-'}{formatCLP(m.monto)}
                  </span>
                </li>
              );
            })}
          </ul>
        </div>
      ) : (
        <div style={{ textAlign: 'center', padding: '40px 20px', color: 'var(--text-muted)', background: 'white', borderRadius: 12 }}>
          No hay movimientos en {MESES[selectedMonth - 1]} {selectedYear}
        </div>
      )}
    </div>
  );
}

export default Reports;
